import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Arrange, Button, Link } from "@aviary";
import { cookieExtractor } from "@shared/helpers/cookieExtractor/cookieExtractor";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { l } from "@shared/locales/i18n";

import * as styles from "./CookieBanner.styles";

interface Props {
  isUnAuthPage?: boolean;
}

const CookieBanner = ({ isUnAuthPage }: Props) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { t } = useTranslation();
  const CONSENT_COOKIE = "eev2_cookie";

  const { tablet } = useBreakpoints();

  const handleConsent = (response: "granted" | "denied") => {
    const expiry = new Date();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    const startDate = new Date().getTime();
    expiry.setTime(expiry.getTime() + thirtyDays);

    document.cookie = `eev2_cookie=${startDate}-${response}; expires=${expiry}; path=/`;
  };

  const handleDecline = () => {
    handleConsent("denied");
    setIsVisible(false);

    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: "denied",
      });
    }
  };

  const handleAccept = () => {
    handleConsent("granted");
    setIsVisible(false);

    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: "granted",
      });
    }
  };

  useEffect(() => {
    if (document.readyState === "complete" || document.readyState === "interactive") {
      window.dataLayer = window.dataLayer || [];
      const cookieRecord = cookieExtractor([CONSENT_COOKIE]);
      if (CONSENT_COOKIE in cookieRecord) {
        setIsVisible(false);
      } else {
        if (typeof window.gtag === "function") {
          window.gtag("consent", "default", {
            ad_storage: "denied",
          });

          window.gtag("set", "url_passthrough", true);
        }
        setIsVisible(true);
      }
    }
  }, []);

  const renderBannerStyle = () => {
    if (!isUnAuthPage) {
      return [styles.BannerContainer, !tablet.greaterThan && styles.SmallMargin];
    }

    return [styles.BannerContainer, styles.UnAuthContainer];
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div css={renderBannerStyle()} data-testid="emerson-cookie-banner">
      <Arrange isVertical isFullWidth>
        <Typography type="h3">{t(l.cookieBanner.Header)}</Typography>
        <Typography type="body">
          {t(l.cookieBanner.Body)}{" "}
          <Link
            isColor="system"
            href="https://www.emersonecologics.com/privacy-policy"
            noExternalIcon
          >
            {t(l.cookieBanner.LearnMore)}
          </Link>
        </Typography>
      </Arrange>

      <Arrange justify="end">
        <Button intention="system" size="medium" onClick={handleDecline}>
          {t(l.cookieBanner.DeclineButton)}
        </Button>
        <Button intention="primary" size="medium" onClick={handleAccept}>
          {t(l.cookieBanner.AcceptButton)}
        </Button>
      </Arrange>
    </div>
  );
};

export { CookieBanner };
